import { DotsThreeIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';

import { ICON_WEIGHT } from './icon';
import { Popover } from './popover';
import { Button } from './ui/button';

/**
 * A "⋯" button holding the actions a card should offer but not advertise.
 *
 * Rare or destructive actions — breaking a track's link to its entry,
 * removing a flight — sat beside the primary button with equal weight, one
 * slip from the thing the pilot actually came to press. Tucked in here they
 * are still one click away and no longer compete for the glance.
 *
 * Deliberately small: no portal and no roving focus. Each item is an ordinary
 * button in tab order.
 */
export function OverflowMenu({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Popover
      closeOnClick
      panelClassName="flex min-w-44 flex-col p-1"
      trigger={({ open, toggle }) => (
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          aria-expanded={open}
          onClick={toggle}
        >
          <DotsThreeIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
        </Button>
      )}
    >
      {children}
    </Popover>
  );
}

/** One row in an `OverflowMenu`. */
export function OverflowMenuItem({
  onSelect,
  disabled,
  danger,
  children,
}: {
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={`hover:bg-surface-sunken rounded-control flex items-center gap-2 px-2.5 py-1.5 text-left text-sm disabled:opacity-50 ${
        danger === true ? 'text-danger' : 'text-foreground'
      }`}
    >
      {children}
    </button>
  );
}
